import React, { useEffect, useRef, useState } from 'react';
import { View, Image, StyleSheet, Animated, Platform } from 'react-native';
// Remplacer StatusBar par un composant cross-plateforme
const StatusBar = Platform.OS === 'web' ? () => null : require('react-native').StatusBar;
import { useAuth } from '../private/AuthContext';
import AsyncStorage from '@react-native-async-storage/async-storage';

const SplashScreen = ({ navigation }) => {
  const { user, userType, loading: authLoading, lastAuthData } = useAuth();
  const [isReady, setIsReady] = useState(false);
  
  // Animation references
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const scaleAnim = useRef(new Animated.Value(0.3)).current;
  const translateYAnim = useRef(new Animated.Value(0)).current;
  const spinAnim = useRef(new Animated.Value(0)).current;

  // Handle authentication and navigation
  useEffect(() => {
    // Start entrance animation immediately
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 800,
        useNativeDriver: Platform.OS !== 'web',
      }),
      Animated.spring(scaleAnim, {
        toValue: 1,
        friction: 8,
        tension: 40,
        useNativeDriver: Platform.OS !== 'web',
      })
    ]).start();

    // Start continuous rotation animation
    Animated.loop(
      Animated.timing(spinAnim, {
        toValue: 1,
        duration: 1500,
        useNativeDriver: Platform.OS !== 'web',
      })
    ).start();

    // Définir un timeout maximal pour l'affichage du splash (8 secondes)
    const maxSplashTime = setTimeout(() => {
      console.log("Temps maximal d'affichage du splash dépassé, navigation forcée");
      setIsReady(true);
    }, 8000);

    // Wait for authentication to complete and minimum display time
    const minDisplayTime = 2500; // Minimum time to show splash screen (2.5 seconds)
    const startTime = Date.now();
    
    const checkAuthAndNavigate = () => {
      // If auth is still loading, check again in 100ms
      if (authLoading) {
        setTimeout(checkAuthAndNavigate, 150);
        return;
      }
      
      // Calculate how long we've shown the splash screen
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minDisplayTime - elapsedTime);
      
      // Wait for any remaining time, then navigate
      setTimeout(() => {
        setIsReady(true);
      }, remainingTime);
    };
    
    checkAuthAndNavigate();
    
    return () => {
      clearTimeout(maxSplashTime);
    };
  }, [authLoading]);

  // Handle navigation once ready
  useEffect(() => {
    if (isReady) {
      // Determine the initial route based on auth status
      const getInitialRoute = () => {
        if (user) {
          // Si l'utilisateur est connecté, utiliser son type pour la navigation
          switch (userType) {
            case 'admin':
              return 'TabNavigatorAdmin';
            case 'user':
              return 'TabNavigatorUser';
            case 'center':
              return 'TabNavigatorCentre';
            default:
              return 'TabNavigatorUser';
          }
        } else if (lastAuthData && lastAuthData.user) {
          // Si la connexion a échoué mais qu'on a des données récentes, les utiliser
          const weekAgo = Date.now() - (7 * 24 * 60 * 60 * 1000); // 7 jours en millisecondes
          if (lastAuthData.timestamp > weekAgo) {
            console.log("Utilisation de la dernière session connue");

            switch (lastAuthData.userType) {
              case 'admin':
                return 'TabNavigatorAdmin';
              case 'user':
                return 'TabNavigatorUser';
              case 'center':
                return 'TabNavigatorCentre';
              default:
                return 'TabNavigatorUser';
            }
          }
        }

        // Par défaut, aller vers TabNavigatorUser (adapté pour les non-connectés)
        return 'TabNavigatorUser';
      };
      
      // Préparer la navigation
      const nextRoute = getInitialRoute();
      console.log("Navigation vers:", nextRoute);
      
      // Start exit animation and then navigate
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 0,
          duration: 500,
          useNativeDriver: Platform.OS !== 'web',
        }),
        Animated.timing(translateYAnim, {
          toValue: -50,
          duration: 500,
          useNativeDriver: Platform.OS !== 'web',
        }),
      ]).start(() => {
        // Utiliser reset pour éviter les écrans blancs entre les transitions
        navigation.reset({
          index: 0,
          routes: [{ name: nextRoute }],
        });
      });
    }
  }, [isReady, user, userType, navigation, lastAuthData]);

  const spin = spinAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '360deg']
  });

  return (
    <View style={styles.container}>
      <StatusBar hidden={true} />
      <Animated.View
        style={[
          styles.content,
          {
            opacity: fadeAnim,
            transform: [
              { scale: scaleAnim },
              { translateY: translateYAnim }
            ],
          },
        ]}
      >
        <Image
          source={require('../assets/logo.png')}
          style={styles.logo}
        />
      </Animated.View>
      
      <View style={styles.loaderContainer}>
        <Animated.View
          style={[
            styles.spinner,
            {
              transform: [{ rotate: spin }]
            }
          ]}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 100, // S'assurer que le splash reste au premier plan
  },
  content: {
    alignItems: 'center',
  },
  logo: {
    width: 110,
    height: 110,
    resizeMode: 'contain',
  },
  loaderContainer: {
    position: 'absolute',
    bottom: 100,
    width: '80%',
    alignItems: 'center',
  },
  spinner: {
    width: 30,
    height: 30,
    borderWidth: 3,
    borderRadius: 15,
    borderColor: '#E75C25',
    borderTopColor: 'transparent',
    borderRightColor: 'transparent',
  }
});

export default SplashScreen;